import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  MusicNote as MusicIcon,
  Settings as ControlIcon,
  Event as ScheduleIcon,
  Wifi as CloudIcon,
  Security as SecurityIcon,
  Speed as PerformanceIcon,
  Headset as SupportIcon,
  Business as BusinessIcon,
  AudioFile as AudioIcon,
  VolumeUp as VolumeIcon,
  Timer as TimerIcon,
  Equalizer as EqualizerIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ open, onClose }) => {
  const features = [
    {
      icon: <MusicIcon sx={{ color: '#4A90C2' }} />,
      title: 'Multi-Zone Audio Control',
      description: 'Independent control of audio zones throughout your venue. Perfect for hotels, restaurants, and event spaces.'
    },
    {
      icon: <ScheduleIcon sx={{ color: '#2E86AB' }} />,
      title: 'Advanced Event Scheduling',
      description: 'Schedule audio events, announcements, and background music with precision timing and automation.'
    },
    {
      icon: <ControlIcon sx={{ color: '#5BA3D4' }} />,
      title: 'Professional Audio Support',
      description: 'Full ASIO driver support for professional audio interfaces and low-latency performance.'
    },
    {
      icon: <CloudIcon sx={{ color: '#4A90C2' }} />,
      title: 'Cloud Management',
      description: 'Manage your audio system remotely through our secure cloud platform with real-time monitoring.'
    },
    {
      icon: <SecurityIcon sx={{ color: '#2E86AB' }} />,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and security protocols to protect your audio system and data.'
    },
    {
      icon: <PerformanceIcon sx={{ color: '#5BA3D4' }} />,
      title: 'High Performance',
      description: 'Optimized for 24/7 operation with minimal latency and maximum reliability for professional use.'
    }
  ];

  const technicalSpecs = [
    'Multi-channel audio processing (up to 32 channels)',
    'ASIO driver support for professional interfaces',
    'Real-time audio mixing and routing',
    'Schedule-based automation system',
    'Remote monitoring and control',
    'Hotel property management integration',
    'Multiple audio format support (WAV, MP3, FLAC)',
    'Zone-specific volume and EQ control',
    'Emergency announcement system',
    'Backup and failover capabilities'
  ];

  const industries = [
    { name: 'Hotels & Resorts', icon: <BusinessIcon /> },
    { name: 'Restaurants & Bars', icon: <MusicIcon /> },
    { name: 'Event Venues', icon: <ScheduleIcon /> },
    { name: 'Retail Spaces', icon: <VolumeIcon /> },
    { name: 'Corporate Offices', icon: <SecurityIcon /> },
    { name: 'Healthcare Facilities', icon: <SupportIcon /> }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/rafilis-mp-logo.png"
            alt="Rafilis MP"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px'
            }}
          />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Rafilis Multizone Player
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Professional Audio Management System
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Overview Section */}
        <Box sx={{ p: 4, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
            Transform Your Venue's Audio Experience
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.7, mb: 3 }}>
            Rafilis Multizone Player is a comprehensive audio management solution designed for professional venues.
            Control multiple audio zones, schedule events, and deliver exceptional sound experiences with enterprise-grade reliability.
          </Typography>

          {/* Key Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(74, 144, 194, 0.15)',
                    border: '1px solid rgba(74, 144, 194, 0.3)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 600, color: '#1e293b' }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Technical Specifications */}
        <Box sx={{ p: 4, background: '#f8fafc' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Technical Specifications
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <List sx={{ p: 0 }}>
                {technicalSpecs.map((spec, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={spec}
                      primaryTypographyProps={{
                        sx: { fontSize: '0.9rem', color: '#475569' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '12px',
                p: 3,
                color: '#ffffff'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  System Requirements
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  • Windows 10/11 (64-bit)<br />
                  • 8GB RAM minimum<br />
                  • Professional audio interface<br />
                  • Network connectivity<br />
                  • 500MB storage space
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Industries Section */}
        <Box sx={{ p: 4, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Perfect for Every Industry
          </Typography>
          <Grid container spacing={2}>
            {industries.map((industry, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                    border: '1px solid rgba(74, 144, 194, 0.3)'
                  }
                }}>
                  <Box sx={{ color: '#4A90C2', mr: 2 }}>
                    {industry.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {industry.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{
          p: 4,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Transform Your Audio System?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 600, mx: 'auto' }}>
            Join hundreds of venues worldwide using Rafilis Multizone Player for professional audio management.
            Start your free trial today and experience the difference.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip
              label="7-Day Free Trial"
              sx={{
                background: '#10b981',
                color: '#ffffff',
                fontWeight: 600,
                px: 2
              }}
            />
            <Chip
              label="No Credit Card Required"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500,
                px: 2
              }}
            />
            <Chip
              label="24/7 Support"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500,
                px: 2
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AboutModal;